import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { Search, Phone, RefreshCw, UserMinus, StickyNote, X, Undo2 } from 'lucide-react';
import { reconnectionLeadAPI } from '../services/api';
import ResponsiveTable from '../components/common/ResponsiveTable';
import LeadNotesModal, { ReminderBadge } from '../components/common/LeadNotesModal';
import useDarkReady from '../theme/useDarkReady';

// CableTV customers who stopped since January (NOC included), read live from ACTIVE_CATV plus
// the CRM's Connection Close reason. Notes are shared with Reconnection Calls; Box Status edits
// write the sheet; "Remove" only hides the customer from this list.
const PAGE_SIZE = 50;
// What staff can set Box Status to (backend BOX_STATUS_OPTIONS).
const BOX_STATUS_OPTIONS = ['ACTIVE', 'STOP', 'DEACTIVE', 'SUSPEND', 'NOC'];
const EMPTY_FILTERS = { search: '', society: '', month: '', reason: '', boxStatus: '' };
const monthLabel = m => format(new Date(`${m}-01T00:00:00`), 'MMM yyyy');

function CountChips({ counts, active, onPick, label }) {
  return (
    <div className="count-chips">
      <span className="text-muted text-small" style={{ width: 52 }}>{label}</span>
      {counts.map(([k, n, text]) => (
        <button key={k} className={`btn btn-sm ${active === k ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => onPick(active === k ? '' : k)} style={{ padding: '3px 10px', fontSize: '.78rem', whiteSpace: 'nowrap' }}>
          {text || k} <strong style={{ marginLeft: 4 }}>{n}</strong>
        </button>
      ))}
    </div>
  );
}

export default function DiscontinuedCatvPage() {
  useDarkReady();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [notesFor, setNotesFor] = useState(null); // row key
  const [showRemoved, setShowRemoved] = useState(false);

  const load = () => {
    setLoading(true);
    reconnectionLeadAPI.getDiscontinued()
      .then(({ data }) => { setRows(data.data || []); setReconnected(data.reconnected || []); })
      .catch(err => toast.error(err.response?.data?.message || 'Could not load the list'))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  // Customers active in the CRM are never in `rows`. These are the active ones who also paid
  // again while the sheet still says STOP: Sync sets their Box Status to ACTIVE.
  const [reconnected, setReconnected] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const sync = async () => {
    if (!reconnected.length) return toast('The sheet already matches the CRM: nobody to update.');
    const names = reconnected.map(r => `• ${r.name} (${[r.society, r.houseNumber].filter(Boolean).join(' ')})`).join('\n');
    if (!window.confirm(`${reconnected.length} customer(s) are active and paid again in the CRM:\n\n${names}\n\nSet their Box Status to ACTIVE on the sheet and mark them Reconnected?`)) return;
    setSyncing(true);
    try {
      const { data } = await reconnectionLeadAPI.syncDiscontinued();
      const { updated, skipped } = data.data;
      toast.success(`${updated.length} set to ACTIVE on the sheet`);
      if (skipped.length) toast.error(`Not updated: ${skipped.map(s => `${s.name} (${s.why})`).join(', ')}`, { duration: 8000 });
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Sync failed');
    } finally { setSyncing(false); }
  };

  const setFilter = (k, v) => { setFilters(f => ({ ...f, [k]: v })); setPage(1); };
  const patchRow = (key, patch) => setRows(rs => rs.map(r => (r.key === key ? { ...r, ...patch } : r)));

  // Writes the sheet. ACTIVE takes the customer off this list on the next refresh.
  const setBoxStatus = async (row, boxStatus) => {
    if (!window.confirm(`Change Box Status of ${row.name || row.accountNumber} from ${row.boxStatus} to ${boxStatus} on the sheet?`)) return;
    try {
      await reconnectionLeadAPI.setBoxStatus(row.accountNumber, boxStatus);
      patchRow(row.key, { boxStatus });
      toast.success(`Box Status set to ${boxStatus}`);
      if (boxStatus === 'ACTIVE') toast('ACTIVE customers leave this list on the next refresh.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not change Box Status');
    }
  };

  // Removed customers stay off the list (nothing changes on the sheet) until restored from "Removed".
  const setHidden = async (row, hidden) => {
    if (hidden && !window.confirm(`Remove ${row.name || row.accountNumber} from this list?\n\nNothing changes on the sheet. You can bring them back from "Removed".`)) return;
    try {
      await reconnectionLeadAPI.setDiscontinuedHidden(row.leadKey, hidden);
      patchRow(row.key, { hidden });
      toast.success(hidden ? 'Removed from the list' : 'Back on the list');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not update the list');
    }
  };

  const removedCount = rows.filter(r => r.hidden).length;
  // Everything below works on the visible half: the list itself, or the removed ones.
  const visible = useMemo(() => rows.filter(r => r.hidden === showRemoved), [rows, showRemoved]);

  const filtered = useMemo(() => {
    const q = filters.search.trim().toLowerCase();
    return visible.filter(r =>
      (!filters.society || r.society === filters.society)
      && (!filters.month || r.stoppedMonth === filters.month)
      && (!filters.reason || r.reason === filters.reason)
      && (!filters.boxStatus || r.boxStatus === filters.boxStatus)
      && (!q || [r.name, r.accountNumber, r.mobile, r.society, r.houseNumber].some(v => (v || '').toLowerCase().includes(q))));
  }, [visible, filters]);

  // Chip counts follow the other filters, so picking a society shows its months and reasons.
  const countBy = (key, skip) => {
    const c = {};
    visible.filter(r => Object.entries(filters).every(([k, v]) => k === skip || k === 'search' || !v
      || (k === 'month' ? r.stoppedMonth === v : r[k] === v)))
      .forEach(r => { const v = key === 'month' ? r.stoppedMonth : r[key]; c[v] = (c[v] || 0) + 1; });
    return c;
  };
  const monthCounts = Object.entries(countBy('month', 'month')).sort((a, b) => b[0].localeCompare(a[0])).map(([k, n]) => [k, n, monthLabel(k)]);
  const reasonCounts = Object.entries(countBy('reason', 'reason')).sort((a, b) => b[1] - a[1]);
  const societies = useMemo(() => [...new Set(visible.map(r => r.society).filter(Boolean))].sort(), [visible]);
  const boxStatuses = useMemo(() => [...new Set(visible.map(r => r.boxStatus))].sort(), [visible]);

  // Follows the filters, so it's the outstanding of whatever is on screen (e.g. one society).
  const totalDue = filtered.reduce((sum, r) => sum + Math.max(0, r.outstanding || 0), 0);
  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const shown = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const columns = [
    {
      key: 'name', label: 'Customer', title: true,
      render: r => (
        <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 2 }}>
          {r.customerId
            ? <Link to={`/customers/${r.customerId}`} style={{ fontWeight: 600 }}>{r.name || '—'}</Link>
            : <span style={{ fontWeight: 600 }}>{r.name || '—'}</span>}
          {r.accountNumber && <span className="text-muted" style={{ fontSize: '.75rem' }}>{r.accountNumber}</span>}
        </span>
      ),
    },
    {
      key: 'society', label: 'Society / House',
      render: r => (
        <span>
          {r.society || '—'}{r.houseNumber && <span className="text-muted" style={{ whiteSpace: 'nowrap' }}> · {r.houseNumber}</span>}
        </span>
      ),
    },
    {
      key: 'mobile', label: 'Mobile', half: true,
      render: r => r.mobile
        ? <a href={`tel:${r.mobile}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}><Phone size={13} />{r.mobile}</a>
        : '—',
    },
    {
      key: 'outstanding', label: 'Due', half: true,
      render: r => (r.outstanding == null ? <span className="text-muted" title="Not found in the CRM">—</span> : (
        <span style={{ fontWeight: 600, whiteSpace: 'nowrap', color: r.outstanding > 0 ? 'var(--c-b91c1c)' : 'var(--text-muted)' }}>
          ₹{r.outstanding.toLocaleString('en-IN')}
        </span>
      )),
    },
    {
      key: 'reason', label: 'Stopped / Reason', half: true,
      render: r => (
        <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 2 }}>
          <span style={{ fontWeight: 600 }}>{r.reason}</span>
          <span className="text-muted" style={{ fontSize: '.75rem', whiteSpace: 'nowrap' }}
            title={r.stoppedFrom === 'last payment' ? `Approximate: the month after the last payment${r.lastPaidMonth ? ` (${monthLabel(r.lastPaidMonth)})` : ''}` : `From the ${r.stoppedFrom}`}>
            {r.stoppedOn ? format(new Date(`${r.stoppedOn}T00:00:00`), 'dd MMM yyyy') : `~ ${monthLabel(r.stoppedMonth)}`}
          </span>
        </span>
      ),
    },
    {
      key: 'boxStatus', label: 'Box Status / Package', half: true,
      // Editable only when the sheet row has an Account No. to find it by.
      render: r => (
        <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 2 }}>
          {r.accountNumber ? (
            <select className="form-control" value={r.boxStatus} onChange={e => setBoxStatus(r, e.target.value)}
              title="Changes Box Status on the ACTIVE_CATV sheet"
              style={{ padding: '2px 6px', fontSize: '.8rem', fontWeight: 600, minWidth: 96 }}>
              {[...new Set([r.boxStatus, ...BOX_STATUS_OPTIONS])].map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          ) : <span style={{ fontSize: '.8rem', fontWeight: 600 }} title="No Account No. on the sheet, so it can't be changed from here">{r.boxStatus}</span>}
          {r.package && <span className="text-muted" style={{ fontSize: '.75rem', whiteSpace: 'nowrap' }}>{r.package}</span>}
        </span>
      ),
    },
    {
      key: 'details', label: 'Sheet / CRM Note',
      render: r => (
        <span className="text-muted" title={r.details}
          style={{ fontSize: '.8rem', maxWidth: 130, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {r.details || '—'}
        </span>
      ),
    },
    {
      // Remove sits under the note button: its own column made the table wider than a laptop screen.
      key: 'notes', label: 'Notes',
      render: r => {
        const last = r.notes[r.notes.length - 1];
        // Latest reminder still waiting to fire, if any.
        const pending = [...r.notes].reverse().find(n => n.reminderNote?.status === 'pending');
        return (
          <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 2, fontSize: '.72rem' }}>
            <button className="btn btn-secondary btn-sm" onClick={() => setNotesFor(r.key)}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, maxWidth: 120, textAlign: 'left', whiteSpace: 'nowrap' }}
              title={last ? last.text : 'Add a note'}>
              <StickyNote size={13} />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {last ? last.text : 'Add note'}
              </span>
              {r.notes.length > 1 && <span className="text-muted">({r.notes.length})</span>}
            </button>
            <ReminderBadge reminder={pending?.reminderNote} />
            {r.hidden ? (
              <button className="btn btn-secondary btn-sm" onClick={() => setHidden(r, false)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap', marginTop: 2 }}>
                <Undo2 size={13} /> Restore
              </button>
            ) : (
              <button className="btn btn-secondary btn-sm" onClick={() => setHidden(r, true)} title="Remove from this list"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap', color: 'var(--c-b91c1c)', marginTop: 2 }}>
                <X size={13} /> Remove
              </button>
            )}
          </span>
        );
      },
    },
  ];
  const notesRow = notesFor && rows.find(r => r.key === notesFor);

  return (
    <div className="page-content">
      <div className="customers-header mb-4">
        <div>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
            <UserMinus size={18} /> Discontinued CableTV
          </h2>
          <p className="text-muted text-small">
            {rows.length - removedCount} CableTV customers stopped since January (NOC included) · read live from the ACTIVE_CATV sheet
            {reconnected.length > 0 && ` · ${reconnected.length} reconnected (sheet not updated yet)`}
            {totalDue > 0 && ` · ₹${totalDue.toLocaleString('en-IN')} outstanding`}
            {removedCount > 0 && ` · ${removedCount} removed`}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-primary btn-sm" onClick={sync} disabled={loading || syncing}
            title="Customers active and paid again in the CRM get Box Status ACTIVE on the sheet (they are already left out of this list)"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <RefreshCw size={14} /> {syncing ? 'Syncing…' : `Sync with CRM${reconnected.length ? ` (${reconnected.length})` : ''}`}
          </button>
          <button className="btn btn-secondary btn-sm" onClick={load} disabled={loading} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </div>

      {!loading && visible.length > 0 && (
        <div className="card" style={{ padding: '14px 16px', marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <CountChips label="Month" counts={monthCounts} active={filters.month} onPick={v => setFilter('month', v)} />
          <CountChips label="Reason" counts={reasonCounts} active={filters.reason} onPick={v => setFilter('reason', v)} />
        </div>
      )}

      <div className="filter-bar customers-filters">
        <div className="search-input-wrapper customers-search">
          <Search size={14} className="search-icon" />
          <input className="search-input" placeholder="Search name, account no., mobile, society..."
            value={filters.search} onChange={e => setFilter('search', e.target.value)} />
        </div>
        <select className="form-control customers-select" value={filters.society} onChange={e => setFilter('society', e.target.value)}>
          <option value="">All Societies</option>
          {societies.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className="form-control customers-select" value={filters.boxStatus} onChange={e => setFilter('boxStatus', e.target.value)}>
          <option value="">All Box Status</option>
          {boxStatuses.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <button className="btn btn-secondary btn-sm" onClick={() => { setFilters(EMPTY_FILTERS); setPage(1); }}>Reset</button>
        {(removedCount > 0 || showRemoved) && (
          <button className={`btn btn-sm ${showRemoved ? 'btn-primary' : 'btn-secondary'}`} style={{ whiteSpace: 'nowrap' }}
            onClick={() => { setShowRemoved(s => !s); setPage(1); }} title="Customers removed from this list">
            {showRemoved ? 'Back to list' : `Removed (${removedCount})`}
          </button>
        )}
      </div>

      <div className="card">
        <div className="table-container">
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
          ) : shown.length === 0 ? (
            <div className="empty-state">
              <UserMinus size={32} />
              <h3>No customers found</h3>
              <p>Try changing your filters.</p>
            </div>
          ) : (
            <ResponsiveTable columns={columns} rows={shown} rowKey={r => r.key} />
          )}
        </div>

        {pages > 1 && (
          <div className="pagination">
            <span style={{ fontSize: '.8rem', color: 'var(--text-muted)', marginRight: 8 }}>
              Page {page} of {pages} · {filtered.length} total
            </span>
            <button className="page-btn" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>←</button>
            <button className="page-btn" disabled={page >= pages} onClick={() => setPage(p => p + 1)}>→</button>
          </div>
        )}
      </div>

      {notesRow && (
        <LeadNotesModal lead={notesRow} onClose={() => setNotesFor(null)}
          onSaved={d => setRows(rs => rs.map(r => (r.key === notesRow.key ? { ...r, notes: d.notes } : r)))} />
      )}
    </div>
  );
}
